import {
  Encoder,
  buffer2String,
  calculateMD5,
  string2Buffer,
} from "@/utils/tools"
import { geelevel } from "./proto_message"
import { _SegDataChannel } from "./seg_data_channel"
import {} from "./webrtc"
import {
  _MSG_CODE_BEGIN_FILE_TRANSFER,
  _MSG_CODE_BEGIN_FILE_TRANSFER_ACK,
  _MSG_CODE_END_FILE_TRANSFER,
  _MSG_CODE_END_FILE_TRANSFER_ACK,
  _MSG_CODE_FILE_TRANSFER_CONTROL,
  _MSG_CODE_FILE_TRANSFER_DATA,
  _MSG_CODE_FILE_TRANSFER_DATA_ACK,
  _MSG_CODE_SEND_JSON_DATA,
} from "./constants"

// 文件传输控制码
export const SDK_FILE_ACTION_STOP = 1
export const SDK_FILE_ACTION_PAUSE = 2
export const SDK_FILE_ACTION_RESUME = 4

const DATA_CHANNEL_FILE_TRANSPORT = "file-transport"

type SendCustomMessage = (code: number, data?: Uint8Array) => Promise<void>

// 负责管理文件传输任务
export class FileTransferManager {
  // 文件传输任务队列
  readonly tasks = new Map<number, FileTransferTask>()
  // 接收文件进度监听
  readonly recvProgressListeners = new Map<number, OnTransferProgressListener>()
  // 发送文件进度监听
  readonly sendProgressListeners = new Map<number, OnTransferProgressListener>()
  // 接收文件的回调
  private recvFileListener: TransferFileListener | null = null

  // 发送文件的回调
  private sendFileListener: TransferFileListener | null = null

  constructor(
    readonly pc: RTCPeerConnection,
    readonly sendCustomMessage: SendCustomMessage
  ) {}

  // 处理文件传输相关的消息
  processCustomMsg(code: number, data?: Uint8Array) {
    if (!data) {
      console.warn("FileTransferManager processCustomMsg, but data is null")
      return
    }

    switch (code) {
      case _MSG_CODE_BEGIN_FILE_TRANSFER: {
        // 开始接收远端的文件
        this.onBeginRecvFile(data)
        break
      }
      case _MSG_CODE_BEGIN_FILE_TRANSFER_ACK: {
        // 开始发送文件到远端
        this.onBeginSendFile(data)
        break
      }
      case _MSG_CODE_FILE_TRANSFER_DATA_ACK: {
        // 收到发送文件数据的 ACK
        this.onFileSendAck(data)
        break
      }
      case _MSG_CODE_END_FILE_TRANSFER_ACK: {
        // 发送文件完毕后，收到远端的确认 ACK
        this.onFileSendEnd(data)
        break
      }
      case _MSG_CODE_FILE_TRANSFER_CONTROL: {
        // 文件传输控制
        this.onFileControl(data)
        break
      }
    }
  }

  // 初始化接收任务的 DataChannel
  initRecvDataChannel(event: RTCDataChannelEvent) {
    if (!event.channel.label.startsWith(DATA_CHANNEL_FILE_TRANSPORT)) {
      return
    }

    // 获取任务 ID
    const taskId = parseInt(event.channel.label.split("_")[1])
    const task = this.tasks.get(taskId)
    if (!task) {
      console.error(
        `FileTransferManager initRecvDataChannel, task ${taskId} not found`
      )
      return
    }

    // 初始化接收任务的 DataChannel
    const recvTask = task as RecvFileTask
    recvTask.initDataChannel(event)
  }

  // 连接断开时调用
  clear(): void {
    // 清理文件传输任务队列
    this.tasks.forEach((task) => {
      task.stop()
    })
    this.tasks.clear()
  }

  /** 添加发送文件任务（本地 -> 远端）
   * @param file 要发送的本地文件
   *
   * @return 添加任务成功时返回文件传输任务 ID，失败则返回null
   */
  addSendFileTask(file: File): number | null {
    let isAlreadyTransfer = false
    this.tasks.forEach((task, _) => {
      if (task instanceof SendFileTask && task.isTranferFile(file)) {
        isAlreadyTransfer = true
      }
    })
    if (isAlreadyTransfer) {
      console.error(
        `FileTransferManager addSendFileTask, ${file.name} is already in transfer`
      )
      return null
    }

    // 创建文件发送任务
    const task = new SendFileTask({
      pc: this.pc,
      file,
      sendCustomMessage: this.sendCustomMessage,
    })
    // 添加到任务队列
    this.tasks.set(task.id, task)

    // 通知创建接收文件任务
    this.sendFileListener?.onNewTask({
      taskId: task.id,
      fileName: file.name,
      fileSize: file.size,
    })
    task.setProgressListener((progress) => {
      // 通知文件传输进度
      this.sendProgressListeners.get(task.id)?.(progress)
    })

    task.setOnCompleteListener(() => {
      // 通知文件传输任务完成
      this.sendFileListener?.onComplete(task.id)
      // 清理进度监听
      this.sendProgressListeners.delete(task.id)
    })
    return task.id
  }

  setRecvFileListener(listener: TransferFileListener) {
    this.recvFileListener = listener
  }

  setSendFileListener(listener: TransferFileListener) {
    this.sendFileListener = listener
  }

  // 设置接收文件进度回调监听
  setFileRecvProgressListener(
    taskId: number,
    listener: OnTransferProgressListener
  ) {
    this.recvProgressListeners.set(taskId, listener)
  }

  // 设置发送文件进度回调监听
  setFileSendProgressListener(
    taskId: number,
    listener: OnTransferProgressListener
  ) {
    this.sendProgressListeners.set(taskId, listener)
  }

  // 停止某个任务
  stopTask(id: number) {
    const task = this.tasks.get(id)
    if (!task) {
      console.error(`FileTransferManager stopTask, task ${id} not found`)
      return
    }
    task.stop()
    this.tasks.delete(id)
  }

  // 开始接收文件
  private onBeginRecvFile(data: Uint8Array) {
    try {
      // 解析文件名，文件大小
      const fileTransferBegin = geelevel.protobuf.FileTransferBegin.decode(data)
      const taskId: number = fileTransferBegin.id
      const fileName: string = buffer2String(
        fileTransferBegin.name as Uint8Array,
        "GBK"
      )
      const size: number = fileTransferBegin.size
      // 创建传输任务，并添加到任务队列
      this.addRecvTask(taskId, fileName, size)
    } catch (e) {
      console.error(
        "FileTransferManager process begin recv file decode error: ",
        e
      )
    }
  }

  // 收到远端 BeginSendFile 的确认消息，开始发送文件
  private onBeginSendFile(data: Uint8Array) {
    try {
      const fileTransferBeginAck =
        geelevel.protobuf.FileTransferBeginAck.decode(data)
      const taskId: number = fileTransferBeginAck.id
      const task = this.tasks.get(taskId)
      if (!task) {
        console.error(
          `FileTransferManager cannot process begin send file ack, task ${taskId} not found`
        )
        return
      }

      // 开始发送文件
      const sendTask = task as SendFileTask
      sendTask.start()
    } catch (e) {
      console.error(
        "FileTransferManager process begin send file ack decode error: ",
        e
      )
    }
  }

  // 收到远端 FileTransferData 响应消息的回调
  private onFileSendAck(data: Uint8Array) {
    try {
      const fileTransferDataAck =
        geelevel.protobuf.FileTransferDataAck.decode(data)
      const taskId: number = fileTransferDataAck.id
      const task = this.tasks.get(taskId)
      if (!task) {
        // console.error(
        //   `FileTransferManager cannot process file transfer data ack, task ${taskId} not found`
        // )
        return
      }

      const sendTask = task as SendFileTask
      // 通知文件传输任务，远端已经收到了文件段数据
      sendTask.onAck()
    } catch (e) {
      console.error(
        "FileTransferManager file transfer data ack decode error: ",
        e
      )
    }
  }

  // 收到远端 FileTransferEnd 响应消息的回调
  private onFileSendEnd(data: Uint8Array) {
    try {
      const fileTransferEndAck =
        geelevel.protobuf.FileTransferEndAck.decode(data)
      const taskId: number = fileTransferEndAck.id
      const task = this.tasks.get(taskId)
      if (!task) {
        console.error(
          `FileTransferManager cannot process file transfer end ack, task ${taskId} not found`
        )
        return
      }
      // 停止传输任务
      task.stop()
      // 删除已完成的文件传输任务
      this.tasks.delete(taskId)
    } catch (e) {
      console.error(
        "FileTransferManager file transfer end ack decode error: ",
        e
      )
    }
  }

  // 收到远端 FileControl 响应消息时的回调
  private onFileControl(data: Uint8Array) {
    try {
      const fileControl = geelevel.protobuf.FileTransferControl.decode(data)
      const taskId: number = fileControl.id
      const task = this.tasks.get(taskId)
      if (!task) {
        console.error(
          `FileTransferManager cannot process file control ack, task ${taskId} not found`
        )
        return
      }
      // 文件传输控制
      const action: number = fileControl.action
      task.control(action)

      // 清除停止传输的任务
      if (action === SDK_FILE_ACTION_STOP) {
        this.tasks.delete(taskId)
      }
    } catch (e) {
      console.error("FileTransferManager file control ack decode error: ", e)
    }
  }

  /**
   * 添加接收文件任务
   *
   * @param id 任务 ID (由发送端创建)
   * @param fileName 保存到本地的文件名
   * @param size 文件大小 (字节数)
   */
  private async addRecvTask(id: number, fileName: string, size: number) {
    if (size === 0) {
      return
    }

    if (this.tasks.has(id)) {
      return
    }

    // 创建文件接收任务
    const task = new RecvFileTask({
      id,
      fileName,
      fileSize: size,
      sendCusomMessage: this.sendCustomMessage,
    })
    // BeginAck 发出后远端可能立即创建 DataChannel，先注册任务避免丢失首包。
    this.tasks.set(id, task)
    // 准备接收文件
    const isSuccess = await task.prepare()
    if (!isSuccess) {
      // 准备失败
      this.tasks.delete(id)
      return
    }
    // 通知创建接收文件任务
    this.recvFileListener?.onNewTask({
      taskId: id,
      fileName,
      fileSize: size,
    })
    task.setProgressListener((progress) => {
      // 通知文件传输进度
      this.recvProgressListeners.get(id)?.(progress)
    })
    task.setOnCompleteListener(() => {
      // 文件传输完毕后清理任务
      this.tasks.delete(id)
      // 通知文件传输任务完成
      this.recvFileListener?.onComplete(id)
      // 清理进度监听
      this.recvProgressListeners.delete(id)
    })
  }

  // 发送开始传输文件（本地到远端）的消息
  // @return 返回传输任务id
  beginSendFile(file: File) {
    // 创建发送文件任务，并添加到任务队列
    const taskId = this.addSendFileTask(file)
    if (taskId === null) {
      // 添加发送文件任务失败
      return
    }

    try {
      const encoder: Encoder = "UTF-8"
      const fileNameBuf = string2Buffer(file.name, encoder)
      const msg = geelevel.protobuf.FileTransferBegin.create({
        id: taskId,
        name: fileNameBuf,
        size: file.size,
      })
      // 发送 begin file transfer 消息
      const buffer = geelevel.protobuf.FileTransferBegin.encode(msg).finish()
      this.sendCustomMessage(_MSG_CODE_BEGIN_FILE_TRANSFER, buffer)
    } catch (e) {
      console.error("WebRTC send begin file encode error: ", e)
    }
  }

  // 从远端选择文件传输到本地
  selectRecvFile() {
    const data = { type: "file", op: "send", msg_id: 2, time: Date.now() }
    try {
      const jsonData = geelevel.protobuf.JsonData.create({
        data: JSON.stringify(data),
      })
      const buffer = geelevel.protobuf.JsonData.encode(jsonData).finish()
      this.sendCustomMessage(_MSG_CODE_SEND_JSON_DATA, buffer)
    } catch (e) {
      console.error("WebRTC select receive file data encode error: ", e)
    }
  }
}

// 用于传输文件
export abstract class FileTransferTask {
  // 文件传输的 data channel
  protected fileChannel?: _SegDataChannel
  // 分段传输时的偏移量
  protected pos: number = 0

  // 当前累计的传输 byte 数 (单位为s)
  private curBps: number = 0
  // 上一次累计的 byte 数 (单位为s)
  private lastBps: number = 0
  // 当前开始累计的时间戳
  private bpsTime: number = 0

  // 传输完毕的回调
  protected onComplete: (() => void) | null = null
  // 传输进度回调
  protected onProgress: OnTransferProgressListener | null = null

  // 传输速度 Bytes/S
  protected get speed(): number {
    const now = Date.now()
    if (now - this.bpsTime < 1000) {
      return this.lastBps
    } else {
      return 0
    }
  }

  protected get isReachedMaxSpeed(): boolean {
    return this.curBps >= MAX_SPEED
  }

  // 达到限速后需要等待的时间 (单位：ms)
  protected get waitMSForRetry(): number {
    return Math.max(0, 1000 - (Date.now() - this.bpsTime))
  }

  // 更新传输速度统计数据
  protected updateBps(bytes: number) {
    const now = Date.now()
    if (now - this.bpsTime < 1000) {
      // 距上次统计不到1s
      this.curBps += bytes
      return
    }
    // 更新上一秒累计传输 bit 数
    this.bpsTime = Date.now()
    this.lastBps = this.curBps
    // 清空当前累计传输 bit 数
    this.curBps = bytes
  }

  // 设置传输结束监听
  setOnCompleteListener(listener: () => void) {
    this.onComplete = listener
  }

  // 设置传输进度监听
  setProgressListener(listener: OnTransferProgressListener) {
    this.onProgress = listener
  }

  // 停止传输文件
  abstract stop(): void

  // 传输控制
  abstract control(action: number): void
}

enum FileTransferStatus {
  stop,
  running,
  pause,
}

// 最大传输速度 单位：Bytes/S
const MAX_SPEED = 1024 * 1024

interface SendFileTaskProps {
  pc: RTCPeerConnection
  sendCustomMessage: SendCustomMessage
  file: File
}

// 负责发送本地文件到远端
export class SendFileTask extends FileTransferTask {
  readonly pc: RTCPeerConnection
  readonly sendCustomMessage: SendCustomMessage

  private reader?: FileReader
  // 累计传输的字节数
  private sendPkgNum = 0
  private recvPkgNum = 0
  // 传输任务id
  readonly id: number
  // 发送的文件
  readonly file: File

  // 最大允许的已发送但未被接收的数据量
  private readonly maxUnrecvDataSize = 512 * 1024
  // 未被接收的数据量低于此值时，可以继续发送文件
  private readonly unrecvDataLowThreshold = 1024 * 64
  // 文件传输分段大小 4K
  private readonly fileChunkSize = 4 * 1024
  // 传输状态
  protected status: FileTransferStatus = FileTransferStatus.stop

  get isRunning(): boolean {
    return this.status === FileTransferStatus.running
  }

  get isPaused(): boolean {
    return this.status === FileTransferStatus.pause
  }

  get isStoped(): boolean {
    return this.status === FileTransferStatus.stop
  }

  // 定时器id，用于通知传输进度
  private progressTimer: number | null = null

  constructor({ pc, file, sendCustomMessage }: SendFileTaskProps) {
    super()
    this.pc = pc
    this.file = file
    this.sendCustomMessage = sendCustomMessage
    this.id = Date.now()
  }

  // 初始化 Data channel
  async initDataChannel(): Promise<void> {
    const channelName = `${DATA_CHANNEL_FILE_TRANSPORT}_${this.id}`
    this.fileChannel = new _SegDataChannel(
      this.pc.createDataChannel(channelName)
    )
    await this.fileChannel.waitUntilOpen()
  }

  // 开始发送文件
  async start() {
    // 初始化 Data Channel
    await this.initDataChannel()

    // 初始化传输相关参数
    this.pos = 0
    this.status = FileTransferStatus.running

    this.reader = new FileReader()
    // 读取文件中
    this.reader.onload = (event) => this.onFileLoad(event)
    // 读取文件失败
    this.reader.onerror = (event) => {
      console.error("SendFileTask read file error:", event)
    }

    // 开始读取文件
    this.readFileChunk()

    // 追踪传输进度
    if (this.progressTimer === null) {
      this.progressTimer = window.setInterval(() => {
        if (this.onProgress) {
          this.onProgress({
            curRecv: this.pos,
            speed: this.speed,
          })
        }
      }, 1000)
    }
  }

  // 对端确认收到文件段数据的回复
  onAck(): void {
    this.recvPkgNum++

    if (this.isPaused && this.isStackedPkgsLow()) {
      // 继续读取文件
      this.readFileChunk()
      this.status = FileTransferStatus.running
    }
  }

  // 读取一段文件数据
  private readFileChunk() {
    if (this.isStoped || !this.reader) {
      return
    }

    if (this.pos >= this.file.size) {
      console.warn("SendFileTask read file end")
      this.sendFileEnd()
      return
    }

    // 读取到的位置
    const readTo = Math.min(this.pos + this.fileChunkSize, this.file.size)
    // 开始读取一段文件
    const chunk = this.file.slice(this.pos, readTo)
    this.reader.readAsArrayBuffer(chunk)
    this.pos = readTo
  }

  // 读取一段文件后的回调
  private onFileLoad(event: ProgressEvent<FileReader>) {
    if (this.isStoped) {
      return
    }

    const buffer = event.target?.result
    if (!buffer) {
      console.error("SendFileTask read file error, file buffer is undefined")
      return
    }

    // 发送文件数据到远端
    this.sendFileData(buffer as ArrayBuffer)

    if (this.isReachedMaxSpeed) {
      // 达到限速，等待一段时间后继续发送
      setTimeout(() => {
        this.readFileChunk()
      }, this.waitMSForRetry)
      return
    }

    if (this.isStackTooManyPkgs()) {
      // 堆积了太多未收到 Ack 的包，暂停读取文件
      this.status = FileTransferStatus.pause
      return
    }

    if (this.pos < this.file.size) {
      this.readFileChunk()
    } else {
      // 文件发送完毕，发送消息通知远端
      this.sendFileEnd()
    }
  }

  // Data Channel 发送缓冲区消耗完毕的回调
  // private onBufferedAmountlow() {
  //   if (this.isStoped) {
  //     return
  //   }
  //   if (this.isPaused) {
  //     // 继续读取文件
  //     this.readFileChunk()
  //     this.status = FileTransferStatus.running
  //   }
  // }

  // 是否堆积了过多未收到 Ack 的包
  private isStackTooManyPkgs(): boolean {
    // 当未被接收的数据量过大，应该暂停传输
    return (
      (this.sendPkgNum - this.recvPkgNum) * this.fileChunkSize >
      this.maxUnrecvDataSize
    )
  }

  // 未收到的 Ack 的包是否低于阈值
  private isStackedPkgsLow(): boolean {
    return (
      (this.sendPkgNum - this.recvPkgNum) * this.fileChunkSize <
      this.unrecvDataLowThreshold
    )
  }

  // 传输一段文件数据
  private sendFileData(buf: ArrayBuffer) {
    if (!this.fileChannel || this.isStoped || buf.byteLength === 0) {
      return
    }

    const fileData = geelevel.protobuf.FileTransferData.create({
      id: this.id,
      md5: calculateMD5(buf),
      data: new Uint8Array(buf),
    })
    const buffer = geelevel.protobuf.FileTransferData.encode(fileData).finish()
    this.fileChannel.send(_MSG_CODE_FILE_TRANSFER_DATA, buffer)
    this.sendPkgNum++
    // 更新传输速度
    this.updateBps(buffer.byteLength)
  }

  // 通知远端文件发送完成
  private sendFileEnd() {
    if (!this.fileChannel || this.isStoped) {
      return
    }

    const fileEnd = geelevel.protobuf.FileTransferEnd.create({
      id: this.id,
    })
    const buffer = geelevel.protobuf.FileTransferEnd.encode(fileEnd).finish()
    this.fileChannel.send(_MSG_CODE_END_FILE_TRANSFER, buffer)
    this.status = FileTransferStatus.stop
    if (this.onComplete) {
      this.onComplete()
    }
  }

  // 传输控制
  control(action: number) {
    if (action === SDK_FILE_ACTION_STOP) {
      // 停止传输
      this.stop()
    }
  }

  // 停止传输
  stop() {
    this.status = FileTransferStatus.stop
    this.fileChannel?.close()
    if (this.progressTimer !== null) {
      window.clearInterval(this.progressTimer)
    }
  }

  // 是否正在传输此文件
  isTranferFile(file: File): boolean {
    return this.file.name === file.name
  }
}

interface RecvFileTaskProps {
  id: number
  fileName: string
  fileSize: number
  sendCusomMessage: SendCustomMessage
}

// 负责从远端接收文件
export class RecvFileTask extends FileTransferTask {
  // 写入文件的流
  private fileWriter: FileSystemWritableFileStream | null = null
  // 任务 ID
  readonly id: number
  // 文件名
  readonly fileName: string
  // 文件大小
  readonly fileSize: number

  // 定时器id，用于通知传输进度
  private progressTimer: number | null = null
  // DataChannel 不会等待异步 message 回调；串行写入才能保证文件块不乱序。
  private writeQueue: Promise<void> = Promise.resolve()
  private receivedEnd = false

  readonly sendCustomMessage: SendCustomMessage

  // 是否传输完成
  private get isComplete(): boolean {
    return this.pos === this.fileSize
  }

  constructor({ id, fileName, fileSize, sendCusomMessage }: RecvFileTaskProps) {
    super()
    this.id = id
    this.fileName = fileName
    this.fileSize = fileSize
    this.sendCustomMessage = sendCusomMessage
  }

  // 初始化 DataChannel
  initDataChannel(event: RTCDataChannelEvent) {
    this.fileChannel = new _SegDataChannel(event.channel)
    this.fileChannel.setOnMessageListener((msgCode, data) => {
      switch (msgCode) {
        case _MSG_CODE_FILE_TRANSFER_DATA:
          // 接收到文件数据
          this.enqueueRecvFileData(data)
          break
        case _MSG_CODE_END_FILE_TRANSFER:
          // 接收文件完毕
          void this.onRecvFileEnd()
          break
      }
    })
  }

  /**
   * 准备接收文件
   * @returns 新建文件的路径，如果失败则返回 null
   */
  async prepare(): Promise<boolean> {
    try {
      // 创建文件写入流
      const fd = await window.showSaveFilePicker({
        suggestedName: this.fileName,
        // startIn: "downloads",
      })
      this.fileWriter = await fd.createWritable()
    } catch (e) {
      // 通知远端终止任务
      this.sendRecvEndAck()
      return false
    }

    // 通知远端开始发送文件
    const msg = geelevel.protobuf.FileTransferBeginAck.create({
      id: this.id,
      result: 0,
    })
    const buffer = geelevel.protobuf.FileTransferBeginAck.encode(msg).finish()
    this.sendCustomMessage(_MSG_CODE_BEGIN_FILE_TRANSFER_ACK, buffer)

    // 追踪传输进度
    if (this.progressTimer === null) {
      this.progressTimer = window.setInterval(() => {
        if (this.onProgress) {
          this.onProgress({
            curRecv: this.pos,
            speed: this.speed,
          })
        }
      }, 1000)
    }
    return true
  }

  control(action: number): void {
    if (action === SDK_FILE_ACTION_STOP) {
      // 停止传输
      this.stop()
    }
  }

  // 文件传输完毕
  private async complete(): Promise<void> {
    await this.clear()
    this.onComplete?.()
  }

  // 中断文件传输
  stop(): void {
    // 通知远端终止任务
    this.sendRecvEndAck()
    // 清理资源
    void this.clear(true).catch((error) => {
      console.error("Abort received file transfer error: ", error)
    })
  }

  // 清理资源
  private async clear(isAbort: boolean = false): Promise<void> {
    // 清理进度监听
    if (this.progressTimer !== null) {
      window.clearInterval(this.progressTimer)
    }
    // 清理文件流
    if (isAbort) {
      await this.fileWriter?.abort()
    } else {
      await this.fileWriter?.close()
    }
    this.fileWriter = null
    // 关闭数据通道
    this.fileChannel?.close()
  }

  private enqueueRecvFileData(data?: Uint8Array) {
    if (this.receivedEnd) return
    this.writeQueue = this.writeQueue.then(() => this.onRecvFileData(data))
    void this.writeQueue.catch((error) => {
      console.error("Write received file data error: ", error)
      this.stop()
    })
  }

  // 收到远端发送的文件传输数据
  private async onRecvFileData(data?: Uint8Array) {
    if (this.fileWriter === null) {
      console.error("RecvFileTask onRecvFileData, file writer is null")
      return
    }
    if (!data) {
      console.error("RecvFileTask onRecvFileData, data is null")
      return
    }

    try {
      // 获取文件传输任务
      const fileTransferData = geelevel.protobuf.FileTransferData.decode(data)
      const taskId: number = Number(fileTransferData.id)
      if (taskId !== this.id) {
        throw new Error(`Received data for unexpected task ${taskId}`)
      }

      // 校验文件数据
      const md5: string = fileTransferData.md5
      const fileData = new Uint8Array(fileTransferData.data as Uint8Array)
      const fileHash = calculateMD5(fileData)
      if (fileHash !== md5) {
        throw new Error("Received file data failed the MD5 check")
      }

      // 写入文件
      const fileChunkSize = fileData.byteLength
      if (this.pos + fileChunkSize > this.fileSize) {
        throw new Error("Received file data exceeds the expected file size")
      }
      await this.fileWriter.write({
        type: "write",
        position: this.pos,
        data: fileData,
      })
      this.updateBps(fileChunkSize)
      this.pos += fileChunkSize

      // 块已持久化后再 ACK，防止发送速度超过浏览器写入速度。
      const msg = geelevel.protobuf.FileTransferDataAck.create({
        id: taskId,
        result: 0,
      })
      const buffer = geelevel.protobuf.FileTransferDataAck.encode(msg).finish()
      await this.sendCustomMessage(_MSG_CODE_FILE_TRANSFER_DATA_ACK, buffer)
    } catch (e) {
      console.error("RecvFileTask onRecvFileData error: ", e)
      throw e
    }
  }

  private async onRecvFileEnd() {
    if (this.receivedEnd) return
    this.receivedEnd = true
    try {
      await this.writeQueue
      if (!this.isComplete) {
        throw new Error(
          `Incomplete file transfer: expected ${this.fileSize}, received ${this.pos}`
        )
      }
      await this.complete()
      this.sendRecvEndAck()
    } catch (error) {
      console.error("Complete received file transfer error: ", error)
      this.stop()
    }
  }

  // 发送 End file transfer ack 消息
  private sendRecvEndAck() {
    const msg = geelevel.protobuf.FileTransferEndAck.create({
      id: this.id,
      result: 0,
    })
    const buffer = geelevel.protobuf.FileTransferEndAck.encode(msg).finish()
    this.sendCustomMessage(_MSG_CODE_END_FILE_TRANSFER_ACK, buffer)
  }
}

// 文件接收任务的相关数据
export interface TransferFileTaskData {
  // 任务 ID
  taskId: number
  // 文件名
  fileName: string
  // 文件大小
  fileSize: number
}

// 文件传输任务的相关回调
export interface TransferFileListener {
  // 新建的文件接收任务
  onNewTask: (task: TransferFileTaskData) => void
  // 任务完成
  onComplete: (taskId: number) => void
}

// 文件传输进度回调
export type OnTransferProgressListener = (progress: RecvFileProgress) => void

interface RecvFileProgress {
  // 当前接收到的字节数
  curRecv: number
  // 传输速度 (Bytes per second)
  speed: number
}
